import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Camera, Plus, X, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { teamService } from '@/services/teamService';

interface TeamPhoto {
  id: string;
  team_id: number;
  photo_url: string;
  photo_caption?: string;
  display_order: number;
  created_at: string;
}

interface TeamPhotoGalleryProps {
  teamId: number;
  canManage: boolean;
}

export const TeamPhotoGallery = ({ teamId, canManage }: TeamPhotoGalleryProps) => {
  const [photos, setPhotos] = useState<TeamPhoto[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newPhotoUrl, setNewPhotoUrl] = useState('');
  const [newPhotoCaption, setNewPhotoCaption] = useState('');

  const loadPhotos = async () => {
    try {
      setLoading(true);
      const data = await teamService.getTeamPhotos(teamId);
      setPhotos(data);
    } catch (error) {
      console.error('Error loading team photos:', error);
      toast.error('Failed to load team photos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPhotos(); // Always load photos, regardless of canManage
  }, [teamId]);

  const addPhoto = async () => {
    if (!newPhotoUrl.trim()) {
      toast.error('Please enter a photo URL');
      return;
    }

    try {
      console.log('Adding photo via URL for team:', teamId);
      await teamService.addTeamPhoto(teamId, newPhotoUrl, newPhotoCaption || undefined);
      toast.success('Photo added successfully');
      setNewPhotoUrl('');
      setNewPhotoCaption('');
      setShowAddModal(false);
      loadPhotos(); // Reload photos
    } catch (error) {
      console.error('Error adding photo via URL:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to add photo';
      toast.error(`Failed to add photo: ${errorMessage}`);
    }
  };

  const removePhoto = async (photoId: string) => {
    try {
      await teamService.removeTeamPhoto(photoId);
      toast.success('Photo removed successfully');
      loadPhotos(); // Reload photos
    } catch (error) {
      console.error('Error removing photo:', error);
      toast.error('Failed to remove photo');
    }
  };

  if (loading) {
    return (
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Camera className="h-5 w-5" />
            Team Photos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="aspect-square bg-gray-700 rounded-lg animate-pulse"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Camera className="h-5 w-5" />
              Team Photos ({photos.length}/10)
            </div>
            {canManage && photos.length < 10 && (
              <Button
                onClick={() => setShowAddModal(true)}
                size="sm"
                className="bg-sports-green text-black hover:bg-green-600"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Photo
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {photos.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {photos.map((photo) => (
                <div key={photo.id} className="relative group">
                  <div className="aspect-square bg-gray-700 rounded-lg overflow-hidden">
                    <img
                      src={photo.photo_url}
                      alt={photo.photo_caption || 'Team photo'}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = '/placeholder.svg';
                      }}
                    />
                  </div>
                  
                  {canManage && (
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        onClick={() => removePhoto(photo.id)}
                        size="sm"
                        variant="destructive"
                        className="p-1 h-auto"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                  
                  {photo.photo_caption && (
                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-75 text-white text-xs p-2 rounded-b-lg">
                      {photo.photo_caption}
                    </div>
                  )}
                  
                  {/* Always show external link */}
                  <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      onClick={() => window.open(photo.photo_url, '_blank')}
                      size="sm"
                      variant="outline"
                      className="p-1 h-auto bg-black bg-opacity-50 border-gray-600"
                    >
                      <ExternalLink className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Camera className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-400">No photos added yet.</p>
              {canManage && (
                <Button
                  onClick={() => setShowAddModal(true)}
                  className="mt-4 bg-sports-green text-black hover:bg-green-600"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add First Photo
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Photo Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Add Team Photo</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="photo-url" className="text-white">Photo URL *</Label>
              <Input
                id="photo-url"
                value={newPhotoUrl}
                onChange={(e) => setNewPhotoUrl(e.target.value)}
                placeholder="https://example.com/photo.jpg"
                className="bg-gray-700 border-2 border-sports-green text-white focus:border-sports-green focus:ring-sports-green"
              />
            </div>
            
            <div>
              <Label htmlFor="photo-caption" className="text-white">Caption (optional)</Label>
              <Input
                id="photo-caption"
                value={newPhotoCaption}
                onChange={(e) => setNewPhotoCaption(e.target.value)}
                placeholder="Describe this photo..."
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            
            {newPhotoUrl && (
              <div>
                <Label className="text-white">Preview</Label>
                <div className="aspect-video bg-gray-700 rounded-lg overflow-hidden mt-2">
                  <img
                    src={newPhotoUrl}
                    alt="Preview"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = '/placeholder.svg';
                    }}
                  />
                </div>
              </div>
            )}
            
            <div className="flex justify-end gap-2">
              <Button
                onClick={() => setShowAddModal(false)}
                variant="outline"
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                Cancel
              </Button>
              <Button
                onClick={addPhoto}
                disabled={!newPhotoUrl.trim()}
                className="bg-sports-green text-black hover:bg-green-600 disabled:bg-gray-600 disabled:text-gray-400"
              >
                Add Photo
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};